import { useState, useEffect, useMemo } from 'react'
import { funnelApi } from '../api'
import type { FunnelAnalytics } from '../types'

/** Fetch per-funnel analytics for a list of funnel IDs in parallel */
export function useFunnelAnalyticsMap(funnelIds: string[]) {
  const [analyticsMap, setAnalyticsMap] = useState<Map<string, FunnelAnalytics>>(new Map())
  const [loading, setLoading] = useState(false)

  // Stable key to avoid refetching on every render
  const idsKey = useMemo(() => funnelIds.slice().sort().join(','), [funnelIds])

  useEffect(() => {
    if (funnelIds.length === 0) return

    let cancelled = false
    setLoading(true)

    Promise.all(
      funnelIds.map(async (id) => {
        try {
          const response = await funnelApi.getFunnelAnalytics(id)
          return { id, analytics: response.data }
        } catch {
          return null
        }
      })
    ).then((results) => {
      if (cancelled) return
      const map = new Map<string, FunnelAnalytics>()
      for (const result of results) {
        if (result) map.set(result.id, result.analytics)
      }
      setAnalyticsMap(map)
      setLoading(false)
    })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  return { analyticsMap, loading }
}
